/*
  NDI Global JS
  --------------------------------------------------------------
  Site-wide custom behavior. Loaded on every page via theme.liquid.
  Keep this file lean. Page-specific JS belongs in ndi-page-<name>.js.

  Conventions:
    - Vanilla JS only (no jQuery, no bundlers).
    - Define classes as custom elements when state is needed.
    - All public APIs hang off window.NDI to avoid pollution.
*/

window.NDI = window.NDI || {};

NDI.ready = function (cb) {
  if (document.readyState !== 'loading') return cb();
  document.addEventListener('DOMContentLoaded', cb, { once: true });
};

/*
  Google Ads lead attribution: capture gclid + utm_campaign from the landing URL,
  persist 90 days, and auto-fill the hidden fields on the contact/quote forms so
  Shopify emails carry the attribution. Ported lean from the old theme (dropped its
  MutationObserver/popstate machinery — our forms exist at DOMContentLoaded).
*/
NDI.attribution = (function () {
  var DAYS90 = 90 * 24 * 60 * 60 * 1000;

  function store(key, value) {
    if (!value) return;
    var rec = JSON.stringify({ value: value, expiry: Date.now() + DAYS90 });
    try {
      localStorage.setItem(key, rec);
      var exp = new Date(Date.now() + DAYS90).toUTCString();
      // SameSite=None;Secure so it survives the ad-click cross-site landing
      document.cookie = key + '=' + value + '; expires=' + exp + '; path=/; SameSite=None; Secure';
    } catch (e) { /* storage blocked — cookie still set above may also fail; ignore */ }
  }

  function read(key) {
    try {
      var rec = localStorage.getItem(key);
      if (rec) {
        var p = JSON.parse(rec);
        if (Date.now() < p.expiry) return p.value;
      }
    } catch (e) { /* fall through to cookie */ }
    var m = document.cookie.match('(?:^|; )' + key + '=([^;]*)');
    return m ? decodeURIComponent(m[1]) : null;
  }

  function fill(names, value) {
    if (!value) return;
    document.querySelectorAll(names).forEach(function (el) {
      if (!el.value) el.value = value;
    });
  }

  function run() {
    var params = new URLSearchParams(window.location.search);
    // gclid is only valid for Google Ads (gclsrc absent or contains 'aw')
    var gclsrc = params.get('gclsrc');
    if (params.get('gclid') && (!gclsrc || gclsrc.indexOf('aw') !== -1)) {
      store('gclid', params.get('gclid'));
    }
    if (params.get('utm_campaign')) store('utm_campaign', params.get('utm_campaign'));

    fill('input[name="contact[gclid]"]', read('gclid'));
    fill('input[name="contact[utm_campaign]"]', read('utm_campaign'));
  }

  NDI.ready(run);
  return { get: read };
})();

/*
  Saved items — the bookmark from snippets/ndi-save-button.liquid.

  One fetch of the id list per page paints every button; clicks toggle
  optimistically and roll back if the write fails. Endpoints are on the
  ndi-core App Proxy (contract: NDI_ACCOUNT_PORTAL_HANDOFF.md); identity is
  server-side via logged_in_customer_id, so these are plain same-origin
  fetches with no token to manage.
*/
NDI.saved = (function () {
  var BASE = '/apps/ndi-core';

  function all(productId) {
    // Same product can appear as several buttons (PDP + related carousel).
    return document.querySelectorAll('[data-ndi-save="' + productId + '"]');
  }

  function paint(btn, on) {
    btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    var label = on ? btn.dataset.ndiSaveOn : btn.dataset.ndiSaveOff;
    if (label) {
      btn.setAttribute('aria-label', label);
      btn.setAttribute('title', label);
    }
  }

  function paintAll(productId, on) {
    Array.prototype.forEach.call(all(productId), function (b) { paint(b, on); });
  }

  function load() {
    if (!document.querySelector('[data-ndi-save]')) return;
    fetch(BASE + '/portal/wishlist/ids', { headers: { Accept: 'application/json' } })
      .then(function (res) {
        // 404 = endpoint not deployed; anything else failing is equally
        // non-fatal. Buttons just stay unsaved.
        return res.ok ? res.json() : null;
      })
      .then(function (data) {
        if (!data) return;
        (data.ids || []).forEach(function (id) { paintAll(String(id), true); });
      })
      .catch(function () { /* offline — leave everything unsaved */ });
  }

  function toggle(btn) {
    var productId = btn.dataset.ndiSave;
    var was = btn.getAttribute('aria-pressed') === 'true';
    var next = !was;

    paintAll(productId, next);
    btn.setAttribute('aria-busy', 'true');

    fetch(BASE + '/portal/wishlist', {
      method: next ? 'POST' : 'DELETE',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ productId: productId }),
    })
      .then(function (res) {
        // Session expired between page load and click.
        if (res.status === 401 && btn.dataset.ndiSaveLogin) {
          window.location.href = btn.dataset.ndiSaveLogin;
          return;
        }
        if (!res.ok) throw new Error('save_failed');
      })
      .catch(function () { paintAll(productId, was); })
      .then(function () { btn.removeAttribute('aria-busy'); });
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-ndi-save]');
    if (!btn) return;
    e.preventDefault();
    if (btn.hasAttribute('data-ndi-save-guest')) {
      window.location.href = btn.dataset.ndiSaveLogin;
      return;
    }
    toggle(btn);
  });

  NDI.ready(load);
  return { refresh: load };
})();
